export type ContentType = 'pdf' | 'video' | 'audio' | 'image' | 'zip' | 'document';
export type DifficultyLevel = 'iniciante' | 'intermediario' | 'avancado';
export type PaymentStatus = 'pending' | 'completed' | 'failed' | 'refunded';

export interface DigitalContent {
  id: string;
  mentor_id: string;
  title: string;
  description: string;
  content_type: ContentType;
  price_cents: number;
  cover_image_url?: string | null;
  file_url: string;
  file_size_bytes?: number | null;
  file_name?: string | null;
  preview_content?: string | null;
  tags?: string[] | null;
  difficulty_level?: DifficultyLevel | null;
  estimated_duration_minutes?: number | null;
  is_active: boolean;
  is_featured: boolean;
  display_order: number;
  download_count: number;
  game_id?: string | null;
  created_at: string;
  updated_at: string;
  
  // Relacionamentos
  game?: { id: string; name: string; cover_url?: string } | null;
  mentor?: {
    id: string;
    image_url?: string | null;
    profile_id: string;
    profiles?: {
      id: string;
      nickname: string;
      slug?: string | null;
    } | null;
  } | null;
  
  // Campos calculados
  mentor_name?: string;
  mentor_image_url?: string | null;
  mentor_slug?: string | null;
  stats?: DigitalContentStats;
}

export interface DigitalContentStats {
  total_purchases: number;
  total_revenue_cents: number;
  average_rating: number;
  total_reviews: number;
  total_downloads: number;
}

export interface DigitalContentPurchase {
  id: string;
  digital_content_id: string;
  buyer_id: string;
  purchase_price_cents: number;
  payment_status: PaymentStatus;
  payment_method?: string | null;
  stripe_payment_intent_id?: string | null;
  access_granted_at?: string | null;
  download_count: number;
  last_accessed_at?: string | null;
  created_at: string;
  updated_at: string;
  
  // Relacionamentos
  digital_content?: DigitalContent;
}

export interface DigitalContentFile {
  id: string;
  digital_content_id: string;
  file_name: string;
  file_url: string;
  file_type: string;
  file_size_bytes?: number | null;
  display_order: number;
  is_main_file: boolean;
  description?: string | null;
  created_at: string;
}

export interface DigitalContentReview {
  id: string;
  digital_content_id: string;
  reviewer_id: string;
  rating: number;
  review_text?: string | null;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  
  // Relacionamentos
  reviewer?: {
    id: string;
    nickname: string;
    avatar_url?: string | null;
  };
}

// Tipos para criação e edição
export interface DigitalContentCreateData {
  title: string;
  description: string;
  content_type: ContentType;
  price_cents: number;
  cover_image_url?: string | null;
  file_url: string;
  file_size_bytes?: number | null;
  file_name?: string | null;
  preview_content?: string | null;
  tags?: string[];
  difficulty_level?: DifficultyLevel | null;
  estimated_duration_minutes?: number | null;
  game_id?: string | null;
  is_featured?: boolean;
  additional_files?: Omit<DigitalContentFile, 'id' | 'digital_content_id' | 'created_at'>[];
}

export interface DigitalContentUpdateData {
  title?: string;
  description?: string;
  content_type?: ContentType;
  price_cents?: number;
  cover_image_url?: string | null;
  file_url?: string;
  file_size_bytes?: number | null;
  file_name?: string | null;
  preview_content?: string | null;
  tags?: string[];
  difficulty_level?: DifficultyLevel | null;
  estimated_duration_minutes?: number | null;
  is_active?: boolean;
  is_featured?: boolean;
  game_id?: string | null;
}

// Tipos para formulários
export interface DigitalContentFormData {
  title: string;
  description: string;
  content_type: ContentType;
  price_cents: number;
  cover_image_url: string;
  file_url: string;
  file_size_bytes: number;
  file_name: string;
  preview_content: string;
  tags: string[];
  difficulty_level: DifficultyLevel | '';
  estimated_duration_minutes: number;
  is_featured: boolean;
  game_id: string | null;
}

// Tipos para filtros e busca
export interface DigitalContentFilters {
  content_type?: ContentType[];
  difficulty_level?: DifficultyLevel[];
  price_range?: {
    min_cents: number;
    max_cents: number;
  };
  game_id?: string[];
  tags?: string[];
  is_featured?: boolean;
  mentor_id?: string;
}

export interface DigitalContentSearchParams {
  query?: string;
  filters?: DigitalContentFilters;
  sort_by?: 'created_at' | 'price_cents' | 'title' | 'download_count' | 'rating';
  sort_order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

// Tipos para estatísticas do mentor
export interface MentorDigitalContentStats {
  total_contents: number;
  total_sales: number;
  total_revenue_cents: number;
  average_rating: number;
  total_downloads: number;
  contents_by_type: Record<ContentType, number>;
  monthly_revenue: Array<{
    month: string;
    revenue_cents: number;
    sales_count: number;
  }>;
}

// Constantes úteis
export const CONTENT_TYPE_LABELS: Record<ContentType, string> = {
  pdf: 'PDF',
  video: 'Vídeo',
  audio: 'Áudio',
  image: 'Imagem',
  zip: 'Arquivo ZIP',
  document: 'Documento'
};

export const DIFFICULTY_LEVEL_LABELS: Record<DifficultyLevel, string> = {
  iniciante: 'Iniciante',
  intermediario: 'Intermediário',
  avancado: 'Avançado'
};

export const CONTENT_TYPE_ICONS: Record<ContentType, string> = {
  pdf: '📄',
  video: '🎥',
  audio: '🎵',
  image: '🖼️',
  zip: '📦',
  document: '📝'
};

// Funções utilitárias
export const formatPrice = (priceCents: number): string => {
  if (priceCents === 0) {
    return 'Gratuito';
  }
  return (priceCents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const getContentTypeColor = (contentType: ContentType): string => {
  const colors: Record<ContentType, string> = {
    pdf: 'bg-red-500/20 text-red-400',
    video: 'bg-blue-500/20 text-blue-400',
    audio: 'bg-purple-500/20 text-purple-400',
    image: 'bg-green-500/20 text-green-400',
    zip: 'bg-yellow-500/20 text-yellow-400',
    document: 'bg-gray-500/20 text-gray-400'
  };
  
  return colors[contentType] || colors.document;
}; 